import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { createMemoryHistory, createHashHistory } from 'history';

import { Router as ReactRouter } from 'react-router-dom';

import { createWebHistory } from './create-web-history';
import { identityParseRoute } from './identity-parse-route';
import { IRouterConfig } from './types';

const routerConfigKeys = ['parseRoute'];

/**
 * React context storing the router configuration object.
 */
const RouterContext = React.createContext<IRouterConfig>({
  // @ts-expect-error
  [routerConfigKeys]: _.noop,
} as unknown as IRouterConfig);

export const RouterConsumer = RouterContext.Consumer;

interface IRouterProps {
  parseRoute?: (arg: any) => any;
  history?: any;
  [key: string]: any;
}

/**
 * Wrapper around React Router that allows some customization to the default router
 * behavior.
 * @param {object} props - Router configuration options
 * @returns {object}     - Configured router component
 */
export const Router = (props: IRouterProps) => {
  const routerConfig = _.pick(props, routerConfigKeys) as IRouterConfig;
  const remainingProps = _.omit(props, routerConfigKeys) as IRouterProps;

  return (
    <RouterContext.Provider value={routerConfig}>
      <ReactRouter {...remainingProps as any} />
    </RouterContext.Provider>
  );
};

interface MemoryRouterProps {
  initialEntries?: string[];
  parseRoute?: Function;
  [key: string]: any;
}

/*
 * The public API for a <Router> that stores location in memory for testing purpose.
 */
export const MemoryRouter = (props: MemoryRouterProps) => {
  const history = createMemoryHistory(props);
  const routerConfig = _.pick(props, routerConfigKeys);
  return <Router history={history} {...routerConfig as any}>{props.children}</Router>;
};

interface HashRouterProps {
  parseRoute?: Function;
  baseName?: string;
  hashType?: 'hashbang' | 'noslash' | 'slash';
  [key: string]: any;
}

/*
 * The public API for a <Router> that uses window.location.hash.
 */
export const HashRouter = (props: HashRouterProps) => {
  const history = createHashHistory(props);
  const routerConfig = _.pick(props, routerConfigKeys);
  return <Router history={history} {...routerConfig as any}>{props.children}</Router>;
};

Router.propTypes = {
  /**
   * Function that parses the route props provided by React Router into the desired props
   * to send to route-consuming components.
   */
  parseRoute: PropTypes.func,
  /**
   * History object used by React Router for managing history and navigation.
   */
  history: PropTypes.shape({}),
};

Router.defaultProps = {
  parseRoute: identityParseRoute,
  history: createWebHistory(),
};

MemoryRouter.propTypes = {
  parseRoute: PropTypes.func,
  children: PropTypes.node,
};

MemoryRouter.defaultProps = {
  parseRoute: identityParseRoute,
  children: null,
};

HashRouter.propTypes = {
  parseRoute: PropTypes.func,
  children: PropTypes.node,
};

HashRouter.defaultProps = {
  parseRoute: identityParseRoute,
  children: null,
};
